/**
 * License API Client (HA-111)
 * Handles license status checking, polling, and update detection
 */

import axios, { AxiosInstance } from 'axios';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';

/**
 * Response from POST /api/license/check
 */
export interface LicenseStatusCheckResponse {
  practiceId: string;
  tier: string;
  updatedAtUtc: string;
  hasChanged: boolean;
  changedFeatures?: string[];
  action?: 'REFRESH_TOKEN' | 'SHOW_UPGRADE_SUCCESS' | 'SHOW_DOWNGRADE_NOTICE';
}

/**
 * Request body for POST /api/license/check
 */
export interface LicenseStatusCheckRequest {
  lastKnownUpdate?: string;
  lastKnownTier?: string;
}

/**
 * Response from GET /api/license/timestamp
 */
export interface LicenseTimestampResponse {
  tier: string;
  updatedAt: string;
}

/**
 * Information about a client using a seat (HA-157)
 */
export interface ClientSeatInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
  lastActivityDate: string | null;
  isActive: boolean;
  daysSinceActivity: number | null;
}

/**
 * Response from GET /api/license/clients (HA-157)
 */
export interface ClientSeatsResponse {
  practiceId: string;
  clientsUsed: number;
  clientsEntitled: number;
  usagePercentage: number;
  isAtCapacity: boolean;
  clients: ClientSeatInfo[];
  inactiveClientCount: number;
}

/**
 * One plan limit and how much of it is used (HA-453). Admins exclude the practice owner.
 */
export interface LicenseUsage {
  entitled: number;
  used: number;
}

/**
 * Response from GET /api/license/usage: counts the server enforces invites against (HA-453).
 * A limit is null when the plan has none.
 */
export interface LicenseUsageSummaryResponse {
  practiceId: string;
  tier: string;
  seats: LicenseUsage | null;
  clients: LicenseUsage | null;
  practitioners: LicenseUsage | null;
  admins: LicenseUsage | null;
  gracePeriodEnd: string | null;
  isInGracePeriod: boolean;
}

/**
 * Full license allocation from GET /api/license/current
 */
export interface LicenseAllocationResponse {
  practiceId: string;
  tier: string;
  seatsEntitled: number;
  clientsEntitled: number;
  adminsEntitled: number;
  practitionersEntitled: number;
  aiEntitled: number | null;
  telehealthEnabled: boolean;
  groupChatEnabled: boolean;
  groupSchedulingEnabled: boolean;
  fixedDateProgramsEnabled: boolean;
  evergreenProgramsEnabled: boolean;
  smsBroadcastsEnabled: boolean;
  unifiedTeamCalendarEnabled: boolean;
  practiceBrandingEnabled: boolean;
  billingManagementEnabled: boolean;
  auditLogsEnabled: boolean;
  featureFlags: Record<string, boolean> | null;
  gracePeriodEnd: string | null;
  isInGracePeriod: boolean;
  updatedAtUtc: string;
}

/**
 * Creates the license API axios instance with auth interceptor
 */
const createLicenseApi = (): AxiosInstance => {
  const api = axios.create({
    baseURL: `${healioSpaceAPIURL}/api/license`,
    withCredentials: true,
  });

  installSessionInterceptors(api);

  return api;
};

export const licenseApi = createLicenseApi();

/**
 * Check for license updates (polling endpoint)
 * @param lastKnownUpdate - ISO timestamp of last known license update
 * @param lastKnownTier - Last known subscription tier
 */
export const checkLicenseStatus = async (
  lastKnownUpdate?: string,
  lastKnownTier?: string
): Promise<LicenseStatusCheckResponse> => {
  const request: LicenseStatusCheckRequest = {
    lastKnownUpdate,
    lastKnownTier,
  };

  const response = await licenseApi.post<LicenseStatusCheckResponse>('/check', request);

  return response.data;
};

/**
 * Get lightweight license timestamp (for frequent polling)
 */
export const getLicenseTimestamp = async (): Promise<LicenseTimestampResponse> => {
  const response = await licenseApi.get<LicenseTimestampResponse>('/timestamp');

  return response.data;
};

/**
 * Get full license allocation
 */
export const getCurrentLicenseAllocation = async (): Promise<LicenseAllocationResponse> => {
  const response = await licenseApi.get<LicenseAllocationResponse>('/current');

  return response.data;
};

/**
 * Extract the X-License-Updated-At header from any API response
 */
export const extractLicenseUpdatedHeader = (
  headers: Record<string, string>
): string | undefined => {
  return headers['x-license-updated-at'];
};

/**
 * Check if an error response indicates a stale license (LICENSE_STALE)
 */
export const isLicenseStaleError = (error: unknown): boolean => {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    const data = error.response.data as { error?: string } | undefined;

    return data?.error === 'LICENSE_STALE';
  }

  return false;
};

/**
 * Get client seat usage details (HA-157)
 * Returns list of clients using seats to help practitioners manage capacity
 */
export const getClientSeats = async (): Promise<ClientSeatsResponse> => {
  const response = await licenseApi.get<ClientSeatsResponse>('/clients');

  return response.data;
};

/**
 * Get the practice's plan limits and real usage (HA-453)
 */
export const getLicenseUsage = async (): Promise<LicenseUsageSummaryResponse> => {
  const response = await licenseApi.get<LicenseUsageSummaryResponse>('/usage');

  return response.data;
};
